import React, { useState, useEffect, useMemo } from "react";
import { LineChart, StackedBarChart, HorizontalBarChart, fmtAxisNum } from "./charts/TrendCharts";
import S from "../styles";
import EtsySalesTab from "./EtsySalesTab";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const RANGE_OPTIONS = [
  { key: 1, label: "1M" },
  { key: 3, label: "3M" },
  { key: 6, label: "6M" },
  { key: 12, label: "12M" },
  { key: 0, label: "All" },
];

const TABS = [
  { key: "sales", label: "Sales" },
  { key: "expenses", label: "Expenses" },
  { key: "support", label: "Support" },
  { key: "inventory", label: "Inventory" },
];

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (m) => {
  const [y, mo] = m.split("-");
  return `${MONTH_SHORT[parseInt(mo, 10) - 1]} '${y.slice(2)}`;
};
const fmtDollar = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtK = (v) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toLocaleString()}`;

// ─── Stat Card ────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <div style={S.statCard}>
      <span style={S.statLabel}>{label}</span>
      <span style={{ ...S.statValue, color: color || "#0A0A0F", fontSize: 20 }}>{value}</span>
    </div>
  );
}

// ─── Sparse Data Banner ───────────────────────────────
function SparseMessage() {
  return (
    <div style={{ padding: "24px 20px", textAlign: "center", color: "rgba(10,10,15,0.58)", fontSize: 14, background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0}}>
      Limited data — trends improve over time as more orders and expenses are recorded.
    </div>
  );
}

// ─── Chart Card wrapper ───────────────────────────────
function ChartCard({ title, legend, children }) {
  return (
    <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>{title}</h3>
        {legend && (
          <div style={{ display: "flex", gap: 12 }}>
            {legend.map((l) => (
              <span key={l.label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "rgba(10,10,15,0.58)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 0, background: l.color }} /> {l.label}
              </span>
            ))}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// SALES TAB
// ═══════════════════════════════════════════════════════
function ExpensesTab({ store, months }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/trends/expenses?store=${store}&months=${months}`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [store, months]);

  const stats = useMemo(() => {
    if (!data) return {};
    const tbm = data.total_by_month || [];
    const totalSpend = tbm.reduce((s, m) => s + (m.total || 0), 0);
    const avgMonthly = tbm.length > 0 ? totalSpend / tbm.length : 0;

    // Find top category across all months
    const catTotals = {};
    (data.by_category || []).forEach((m) => {
      Object.entries(m).forEach(([k, v]) => {
        if (k !== "month" && k !== "label") catTotals[k] = (catTotals[k] || 0) + v;
      });
    });
    const topCat = Object.entries(catTotals).sort((a, b) => b[1] - a[1])[0];

    return { totalSpend, avgMonthly, topCategory: topCat ? topCat[0] : "—" };
  }, [data]);

  if (loading) return <div style={{ color: "rgba(10,10,15,0.58)", padding: 20 }}>Loading expense data...</div>;
  if (!data || ((data.total_by_month || []).length < 1)) return <SparseMessage />;

  const tbm = data.total_by_month || [];
  const byc = data.by_category || [];

  // Derive category list from data
  const allCats = new Set();
  byc.forEach((m) => Object.keys(m).forEach((k) => { if (k !== "month" && k !== "label") allCats.add(k); }));
  const categories = [...allCats];

  // Category totals for horizontal bar
  const catTotals = {};
  byc.forEach((m) => categories.forEach((c) => { catTotals[c] = (catTotals[c] || 0) + (m[c] || 0); }));
  const catBarData = Object.entries(catTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([ label, value ]) => ({ label, value }));

  const chartData = byc.map((m) => ({ ...m, label: fmtMonth(m.month) }));
  const totalData = tbm.map((m) => ({ label: fmtMonth(m.month), total: m.total }));

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        <StatCard label="Total Spend" value={fmtDollar(stats.totalSpend || 0)} color="#ef4444" />
        <StatCard label="Avg Monthly" value={fmtK(stats.avgMonthly || 0)} />
        <StatCard label="Top Category" value={stats.topCategory} />
      </div>

      <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        <ChartCard title="Monthly Expenses">
          <LineChart data={totalData} valueKey="total" labelKey="label" color="#ef4444" formatValue={fmtK} />
        </ChartCard>

        <ChartCard title="Spend by Category">
          {categories.length > 0 ? (
            <StackedBarChart data={chartData} categories={categories} formatValue={fmtK} />
          ) : (
            <div style={{ padding: 30, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>No category data.</div>
          )}
        </ChartCard>
      </div>

      {catBarData.length > 0 && (
        <ChartCard title="Top Categories (Total)">
          <HorizontalBarChart data={catBarData} formatValue={fmtDollar} />
        </ChartCard>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// SUPPORT TAB
// ═══════════════════════════════════════════════════════
function SupportTab({ store, months }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/trends/support?store=${store}&months=${months}`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [store, months]);

  if (loading) return <div style={{ color: "rgba(10,10,15,0.58)", padding: 20 }}>Loading support data...</div>;
  if (!data) return <SparseMessage />;

  const wv = data.weekly_volume || [];
  const byPlatform = data.by_platform || {};
  const byCategory = data.by_category || {};
  const avgRes = data.avg_resolution_days;
  const openCount = data.open_tickets || 0;

  const platformData = Object.entries(byPlatform)
    .sort((a, b) => b[1] - a[1])
    .map(([label, value]) => ({ label, value }));

  const categoryData = Object.entries(byCategory)
    .sort((a, b) => b[1] - a[1])
    .map(([label, value]) => ({ label, value }));

  const topPlatform = platformData.length > 0 ? platformData[0].label : "—";
  const topCategory = categoryData.length > 0 ? categoryData[0].label : "—";

  // Weekly volume chart: new vs resolved
  const weekChartData = wv.map((w) => ({ label: w.week.replace(/^\d{4}-W/, "W"), new: w.new || 0, resolved: w.resolved || 0 }));
  const weekCategories = ["new", "resolved"];
  const weekColors = { new: "#ef4444", resolved: "#34d399" };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        <StatCard label="Open Tickets" value={openCount} color={openCount > 0 ? "#ef4444" : "#34d399"} />
        <StatCard label="Avg Resolution" value={avgRes != null ? `${avgRes.toFixed(1)} days` : "—"} />
        <StatCard label="Top Platform" value={topPlatform} />
        <StatCard label="Top Category" value={topCategory} />
      </div>

      {weekChartData.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <ChartCard title="Weekly Ticket Volume" legend={[{ label: "New", color: "#ef4444" }, { label: "Resolved", color: "#34d399" }]}>
            <StackedBarChart data={weekChartData} categories={weekCategories} colorMap={weekColors} formatValue={(v) => `${v}`} formatAxis={fmtAxisNum} />
          </ChartCard>
        </div>
      )}

      <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {platformData.length > 0 && (
          <ChartCard title="Tickets by Platform">
            <HorizontalBarChart data={platformData} formatValue={(v) => `${v}`} />
          </ChartCard>
        )}
        {categoryData.length > 0 && (
          <ChartCard title="Tickets by Category">
            <HorizontalBarChart data={categoryData} formatValue={(v) => `${v}`} />
          </ChartCard>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// INVENTORY TAB
// ═══════════════════════════════════════════════════════
function InventoryTab({ store, months }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/trends/inventory?store=${store}&months=${months}`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [store, months]);

  if (loading) return <div style={{ color: "rgba(10,10,15,0.58)", padding: 20 }}>Loading inventory data...</div>;
  if (!data) return <SparseMessage />;

  const velocity = data.velocity || [];
  const restocks = data.restock_frequency || [];
  const lowStockCount = velocity.filter((v) => v.current_stock > 0 && v.days_of_stock < 14).length;
  const mostRestocked = restocks.length > 0 ? restocks[0].name : "—";

  // Avg sell-through: avg units_sold across products that have sales
  const withSales = velocity.filter((v) => v.units_sold > 0);
  const avgSellThrough = withSales.length > 0 ? withSales.reduce((s, v) => s + v.units_sold, 0) / withSales.length : 0;

  const velocityBarData = velocity
    .sort((a, b) => b.units_sold - a.units_sold)
    .slice(0, 10)
    .map((v) => ({ label: v.name, value: v.units_sold, color: "#0033FF" }));

  const restockBarData = restocks
    .slice(0, 10)
    .map((r) => ({ label: r.name, value: r.restock_count, color: "rgba(10,10,15,0.58)" }));

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        <StatCard label="Avg Units Sold" value={avgSellThrough.toFixed(1)} color="#0033FF" />
        <StatCard label="Most Restocked" value={mostRestocked} />
        <StatCard label="Low Stock Items" value={lowStockCount} color={lowStockCount > 0 ? "#ef4444" : "#34d399"} />
      </div>

      <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {velocityBarData.length > 0 && (
          <ChartCard title="Top Products by Units Sold">
            <HorizontalBarChart data={velocityBarData} formatValue={(v) => `${v} units`} />
          </ChartCard>
        )}
        {restockBarData.length > 0 && (
          <ChartCard title="Most Restocked Products">
            <HorizontalBarChart data={restockBarData} formatValue={(v) => `${v}x`} />
          </ChartCard>
        )}
      </div>

      {/* Velocity detail table */}
      {velocity.length > 0 && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F" }}>
            <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>Sell-Through Velocity</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Product</th>
                  <th style={S.th}>SKU</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Units Sold</th>
                  <th style={{ ...S.th, textAlign: "right" }}>In Stock</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Days of Stock</th>
                </tr>
              </thead>
              <tbody>
                {velocity.slice(0, 15).map((v, i) => (
                  <tr key={i}>
                    <td style={S.td}>{v.name}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{v.sku || "—"}</td>
                    <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#0033FF" }}>{v.units_sold}</td>
                    <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{v.current_stock}</td>
                    <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: v.days_of_stock != null && v.days_of_stock < 14 ? "#ef4444" : "#0A0A0F" }}>
                      {v.days_of_stock != null ? v.days_of_stock.toFixed(1) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN STORE TRENDS PAGE
// ═══════════════════════════════════════════════════════
export default function StoreTrendsPage({ store, storeName }) {
  const [tab, setTab] = useState("sales");
  const [months, setMonths] = useState(6);

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Trends</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Sales, expenses, support, and inventory analytics</p>
      </div>

      {/* Controls row */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        {/* Tabs */}
        <div style={S.nav}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}>{t.label}</button>
          ))}
        </div>

        {/* Time range — the Sales tab carries its own window controls */}
        {tab !== "sales" && <div style={S.nav}>
          {RANGE_OPTIONS.map((r) => (
            <button key={r.key} onClick={() => setMonths(r.key)} style={{ ...S.navBtn, ...(months === r.key ? S.navBtnActive : {}), padding: "8px 12px", fontSize: 13 }}>{r.label}</button>
          ))}
        </div>}
      </div>

      {/* Tab content */}
      {tab === "sales" && <EtsySalesTab store={store} />}
      {tab === "expenses" && <ExpensesTab store={store} months={months} />}
      {tab === "support" && <SupportTab store={store} months={months} />}
      {tab === "inventory" && <InventoryTab store={store} months={months} />}
    </div>
  );
}
